/**
 * Le prix fixé par le dentiste (AJ-02, `FONCTION_PRIX_DENTISTE`) : la saisie
 * au box, la liste « à encaisser » de la caisse, l'encaissement de ce montant.
 * Rien de médical : un montant et des libellés de prestations, comme un reçu.
 */
import { z } from 'zod';
import { MOTIF_ANNULATION_MIN } from '../rules/caisse.js';
import { PRESTATIONS_REALISEES_MAX } from '../rules/prix-dentiste.js';
import { schemaMontantAriary, schemaRecu } from './caisse.js';
const schemaQui = schemaRecu.shape.caissiere;
/** Ce que le dentiste saisit : le total en ariary et ce qu'il a fait. */
export const schemaSaisiePrixDentiste = z
    .object({
    montant: schemaMontantAriary.min(1, 'Le prix est un montant positif, en ariary.'),
    prestations: z
        .array(z.string().trim().max(120, 'Un libellé de prestation tient en 120 caractères.'))
        .max(PRESTATIONS_REALISEES_MAX),
})
    .strict();
/** Le prix d'une visite, tel que le dentiste l'a saisi (et peut-être corrigé). */
export const schemaPrixDentiste = z.object({
    id: z.uuid(),
    visiteId: z.uuid(),
    montant: schemaMontantAriary,
    prestations: z.array(z.string()),
    saisiLe: z.iso.datetime(),
    corrigeLe: z.iso.datetime().nullable(),
    /** Le numéro du reçu valide qui l'a encaissé ; `null` tant que la caisse n'a pas encaissé. */
    recuNumero: z.string().nullable(),
});
/**
 * Décision 338 (5) — une visite dentiste du même patient dont le prix est
 * encaissé : la visite d'origine possible d'une suite de soin, sans limite de temps.
 */
export const schemaVisiteOrigine = z.object({
    visiteId: z.uuid(),
    arriveeLe: z.iso.datetime(),
    siteNom: z.string(),
    montant: schemaMontantAriary,
    prestations: z.array(z.string()),
    recuNumero: z.string(),
});
/** « Suite d'un soin déjà payé » : le dentiste choisit la visite d'origine. */
export const schemaSuiteDeSoin = z.object({ visiteOrigineId: z.uuid() }).strict();
/**
 * Sur le patient en consultation (Ma file) : il est non couvert, il paie au
 * dentiste. `prix` est `null` tant que le prix n'est pas saisi — la
 * consultation ne se termine pas avant, sauf si `suiteDe` est renseigné : la
 * suite d'un soin déjà payé se termine sans prix (décision 338).
 */
export const schemaPaiementAuDentiste = z.object({
    prix: schemaPrixDentiste.nullable(),
    /**
     * Décision 338 (5) — la suite d'un soin déjà payé : la visite se termine
     * sans prix. Exclusif de `prix` (le serveur refuse l'un si l'autre est là).
     */
    suiteDe: schemaVisiteOrigine.nullable().optional(),
});
/** Une suggestion pour la saisie : une ligne DENTISTE active de la grille du tarif. */
export const schemaSuggestionPrestation = z.object({
    libelle: z.string(),
    prix: schemaMontantAriary,
});
/** Une ligne de la liste « À encaisser — prix fixé par le dentiste ». */
export const schemaPrixAEncaisser = schemaPrixDentiste.extend({
    patient: schemaRecu.shape.patient,
    numeroPassage: z.string().nullable(),
    dentiste: schemaQui,
    /** La consultation est-elle finie : le dentiste vient en général après. */
    consultationTerminee: z.boolean(),
});
/**
 * Retirer un prix pas encore encaissé (revue g801) : il n'est plus dû — erreur
 * de patient, patient parti. Le dentiste qui l'a fixé, ou la Direction ; un
 * motif lisible, comme l'annulation d'un reçu.
 */
export const schemaRetraitPrixDentiste = z
    .object({
    motif: z
        .string()
        .trim()
        .min(MOTIF_ANNULATION_MIN, `Un motif de ${String(MOTIF_ANNULATION_MIN)} caractères au moins.`)
        .max(300),
})
    .strict();
/** L'encaissement d'un prix fixé : la caisse ne choisit rien, elle dit la somme reçue. */
export const schemaEncaissementPrixDentiste = z
    .object({
    recu: schemaMontantAriary,
    /** Le montant que la caisse a sous les yeux : refus s'il a été corrigé entre-temps. */
    montantVu: schemaMontantAriary,
})
    .strict();
//# sourceMappingURL=prix-dentiste.js.map